import React, { useState } from 'react';
import {
  Dialog,
  Box,
  IconButton,
  Typography,
  Grid,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';

/**
 * ImageCarousel Component - Display multiple laptop images
 * Opens in a modal/dialog when image is clicked
 */
const ImageCarousel = ({ images, open, onClose, laptopName }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Ensure images is an array
  const imageList = Array.isArray(images) ? images : (images ? [images] : []);

  if (imageList.length === 0) return null;

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? imageList.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === imageList.length - 1 ? 0 : prev + 1));
  };

  const currentImage = imageList[currentIndex];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 2,
          bgcolor: 'background.paper',
        }
      }}
    >
      <Box sx={{ position: 'relative', bgcolor: 'grey.900', p: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            {laptopName || 'Laptop Gallery'}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Main Image */}
        <Box
          component="img"
          src={currentImage}
          alt={`${laptopName} - Image ${currentIndex + 1}`}
          sx={{
            width: '100%',
            height: { xs: '300px', md: '500px' },
            objectFit: 'contain',
            bgcolor: 'grey.800',
            borderRadius: 1,
            mb: 2,
          }}
        />

        {/* Navigation */}
        {imageList.length > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <IconButton
              onClick={handlePrevious}
              sx={{
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
              }}
            >
              <ChevronLeftIcon />
            </IconButton>

            <Typography
              variant="body2"
              sx={{
                color: 'white',
                textAlign: 'center',
                flex: 1,
              }}
            >
              {currentIndex + 1} / {imageList.length}
            </Typography>

            <IconButton
              onClick={handleNext}
              sx={{
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        )}

        {/* Thumbnail Strip */}
        {imageList.length > 1 && (
          <Grid container spacing={1}>
            {imageList.map((img, idx) => (
              <Grid item xs={3} sm={2} key={idx}>
                <Box
                  component="img"
                  src={img}
                  alt={`Thumbnail ${idx + 1}`}
                  onClick={() => setCurrentIndex(idx)}
                  sx={{
                    width: '100%',
                    height: '80px',
                    objectFit: 'cover',
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: currentIndex === idx ? '3px solid' : '2px solid transparent',
                    borderColor: currentIndex === idx ? 'primary.main' : 'transparent',
                    opacity: 0.7,
                    transition: 'all 0.2s',
                    '&:hover': {
                      opacity: 1,
                    }
                  }}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Dialog>
  );
};

export default ImageCarousel;
